import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  Container,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CampbellLogo from '../assets/images/campbelllogo_old.png';
import { generateAccessToken, validateUser, createSharePointFolder } from '../services/apiService';

const PasswordModal = ({ onAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isFormLoginEnabled = process.env.REACT_APP_FORM_LOGIN_ENABLED === 'true' ? true : false;

  // Check for existing authentication on component mount
  useEffect(() => {
    const isAuth = localStorage.getItem('authenticated') === 'true';
    const hasValidToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');

    if (isAuth && hasValidToken && userId) {
      onAuthenticated(true);

      // Dispatch personaEvent on page refresh to restore user info
      const storedEmail = localStorage.getItem('userEmail');
      const storedFullName = localStorage.getItem('userFullName');

      if (storedEmail && storedFullName) {
        const personaEvent = new CustomEvent('personaSet', {
          detail: {
            accessToken: hasValidToken,
            userId: userId,
            email: storedEmail,
            full_name: storedFullName
          }
        });
        window.dispatchEvent(personaEvent);
      }
    }
  }, [onAuthenticated]);

  // Check for Microsoft OAuth callback or login parameter
  useEffect(() => {
    if(isFormLoginEnabled === true) return; // Skip if form login is disabled
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const state = urlParams.get('state');
    const msLogin = urlParams.get('mslogin');

    if (authCode && state) {
      // Handle Microsoft OAuth callback
      handleMicrosoftCallback(authCode, state);
    } else if (msLogin === 'true') {
      // Clean up URL parameter and trigger login
      const url = new URL(window.location);
      url.searchParams.delete('mslogin');
      window.history.replaceState({}, document.title, url.pathname + url.search);
      handleMicrosoftLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Automatically trigger Microsoft login on component mount
  useEffect(() => {
    if(isFormLoginEnabled === true) return; // Skip if form login is disabled
    // Skip if already authenticated or if there's a callback/login parameter
    const isAuth = localStorage.getItem('authenticated') === 'true';
    const hasValidToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const msLogin = urlParams.get('mslogin');

    if (!isAuth || !hasValidToken || !userId) {
      if (!authCode && msLogin !== 'true') {
        handleMicrosoftLogin();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (error) setError(''); // Clear error when user types
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (error) setError(''); // Clear error when user types
  };



  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      // Validate email input
      if (!email.trim()) {
        setError('Email is required.');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setError('Please enter a valid email address.');
        return;
      }
      if (password === 'CIG@2025') {
        const result = await handleEmailIdAuthentication(email);
        if (result === "success") {
          generateAccessToken(email, 'default');
          localStorage.setItem('persona', 'default');
          onAuthenticated(true);
        } else if (result?.error) {
          setError(result?.message || 'Incorrect password. Please try again.');
        }
      } else if (password === 'CHIP@2025') {
        const result = await handleEmailIdAuthentication(email);
        if (result === "success") {
          generateAccessToken(email, 'default2');
          localStorage.setItem('persona', 'default2');
          onAuthenticated(true);
        } else if (result?.error) {
          setError(result?.message || 'Incorrect password. Please try again.');
        }

      } else {
        setError('Incorrect password. Please try again.');
      }
    } catch (error) {
      setError('An error occurred during authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 

  const handleEmailIdAuthentication = async (email_id, department = '', firstName = '', lastName = '', designation = '') => {
    const response = await validateUser(email_id, department, firstName, lastName, designation);
    if (response?.access_token && response?.user?.user_id) {



      const fullName = response?.user?.first_name && response?.user?.last_name 
        ? `${response.user.first_name} ${response.user.last_name}` 
        : response?.user?.first_name;

      localStorage.setItem('authenticated', 'true');
      localStorage.setItem('accessToken', response?.access_token);
      localStorage.setItem('userId', response?.user?.user_id);
      localStorage.setItem('userEmail', email_id);
      localStorage.setItem('userFullName', fullName);
      if (response?.user?.role_id != null) {
        localStorage.setItem('userRoleId', String(response.user.role_id));
      }

      onAuthenticated(true);
      let email_address = email_id.toLowerCase();
      // .split('@')[0];
      // Ensure a personal SharePoint folder exists for this user (fire-and-forget)
      createSharePointFolder(email_address, 'Documents', 'fail')
        .catch((err) => console.warn('SharePoint folder creation skipped:', err));

      // Dispatch a custom event to notify other components with complete user info
      const personaEvent = new CustomEvent('personaSet', {
        detail: {
          accessToken: response.access_token,
          userId: response?.user?.user_id,
          email: email_id,
          full_name: fullName,
          persona: localStorage.getItem('persona') || 'default'
        }
      });
      window.dispatchEvent(personaEvent);
      return "success";
    }
    return response;
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleMicrosoftLogin = async () => {
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    const tenantId = process.env.REACT_APP_MICROSOFT_TENANT_ID;
    const redirectUri = process.env.REACT_APP_MICROSOFT_REDIRECT_URI;
    

    // Check if environment variables are configured
    if (!clientId || !tenantId || !redirectUri) {
      setError('Microsoft authentication is not configured. Please contact your administrator.');
      return;
    }

    // Check for placeholder values
    if (clientId === 'your-client-id-here' || tenantId === 'your-tenant-id-here') {
      setError('Microsoft authentication environment variables need to be configured. Please contact your administrator.');
      return;
    }

    try {
      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Construct Microsoft OAuth URL with PKCE
      const scope = 'openid profile email User.Read';
      const responseType = 'code';
      const state = Math.random().toString(36).substring(2, 15); // Generate random state

      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `response_type=${encodeURIComponent(responseType)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `state=${encodeURIComponent(state)}&` +
        `code_challenge=${encodeURIComponent(codeChallenge)}&` +
        `code_challenge_method=S256&` +
        `response_mode=query`;

      // Store state and code verifier for validation when user returns
      localStorage.setItem('msft_auth_state', state);
      localStorage.setItem('msft_code_verifier', codeVerifier);

      // Redirect to Microsoft OAuth
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error setting up Microsoft OAuth:', error);
      setError('Failed to setup Microsoft authentication. Please try again.');
    }
  };

  const handleMicrosoftCallback = async (authCode, state) => {
    try {
      setIsLoading(true);

      // Validate state parameter to prevent CSRF attacks
      const storedState = localStorage.getItem('msft_auth_state');
      if (!storedState) {
        console.warn('No stored state found, proceeding without validation');
      } else if (state !== storedState) {
        setError('Invalid state parameter. Authentication request may have been tampered with.');
        return;
      }

      // Get code verifier
      const codeVerifier = localStorage.getItem('msft_code_verifier');
      if (!codeVerifier) {
        console.warn('No code verifier found, proceeding without PKCE');
      }

      // Exchange authorization code for tokens
      const tokenResponse = await exchangeCodeForTokens(authCode, codeVerifier);
      if (tokenResponse.access_token) {
        // Get user info from Microsoft Graph
        const userInfo = await getMicrosoftUserInfo(tokenResponse.access_token);
        // console.log('Microsoft user info:', userInfo);
        if (userInfo && (userInfo.mail || userInfo.userPrincipalName)) {
          // Store Microsoft access token
          localStorage.setItem('msft_access_token', tokenResponse.access_token);
          if (tokenResponse.refresh_token) {
            localStorage.setItem('msft_refresh_token', tokenResponse.refresh_token);
          }
          
          // Authenticate using the email and user details from Microsoft
          const email = userInfo.mail || userInfo.userPrincipalName || '';
          const department = userInfo.department || '';
          const firstName = userInfo.givenName || '';
          const lastName = userInfo.surname || '';
          const designation = userInfo.jobTitle || '';
          const result = await handleEmailIdAuthentication(email, department, firstName, lastName, designation);
          if (result === "success") {
            // Set Microsoft persona
            localStorage.setItem('persona', 'microsoft');
            
            // Clean up OAuth state
            localStorage.removeItem('msft_auth_state');
            localStorage.removeItem('msft_code_verifier');
            
            // Clean up URL parameters
            const url = new URL(window.location);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState({}, document.title, url.pathname + url.search);
            
            onAuthenticated(true);
          } else {
            setError(result?.message || 'Email authentication failed.');
          }
        } else {
          setError('Unable to retrieve email from Microsoft account.');
        }
      } else {
        setError('Failed to get access token from Microsoft.');
      }
    } catch (error) {
      console.error('Microsoft callback error:', error);
      // setError('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const exchangeCodeForTokens = async (authCode, codeVerifier) => {
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    const tenantId = process.env.REACT_APP_MICROSOFT_TENANT_ID;
    const redirectUri = process.env.REACT_APP_MICROSOFT_REDIRECT_URI;

    const tokenUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`;
    
    const body = new URLSearchParams();
    body.append('client_id', clientId);
    body.append('code', authCode);
    body.append('redirect_uri', redirectUri);
    body.append('grant_type', 'authorization_code');
    
    // Only add code_verifier if it exists (PKCE)
    if (codeVerifier) {
      body.append('code_verifier', codeVerifier);
    }

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error response:', errorText);
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  };

  const getMicrosoftUserInfo = async (accessToken) => {
    const response = await fetch('https://graph.microsoft.com/v1.0/me?$select=mail,userPrincipalName,department,givenName,surname,displayName,jobTitle', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get user info: ${response.statusText}`);
    }

    return response.json();
  };

  // Generate a cryptographically random code verifier
  const generateCodeVerifier = () => {
    const array = new Uint8Array(128);
    window.crypto.getRandomValues(array);
    return base64URLEncode(array);
  };

  // Generate code challenge from verifier using SHA256
  const generateCodeChallenge = async (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return base64URLEncode(digest);
  };

  // Base64 URL encode function
  const base64URLEncode = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={6}
            sx={{
              padding: { xs: 3, sm: 4 },
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Box
              component="img"
              src={CampbellLogo}
              alt="Campbell Logo"
              sx={{
                width: '200px',
                mb: 3,
                mx: 'auto',
              }}
            />

            {isFormLoginEnabled ? (
              <>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontFamily: "'Poppins', sans-serif",
                    color: '#1B2559',
                  }}
                >
                  Welcome Back
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: 'text.secondary',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Please sign in to continue
                </Typography>

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{ mt: 1 }}
                >
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    margin="normal"
                    required
                    autoComplete="email"
                    autoFocus
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handlePasswordChange}
                    margin="normal"
                    required
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      backgroundColor: '#1B2559',
                      '&:hover': { backgroundColor: '#141c45' },
                    }}
                  >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontFamily: "'Poppins', sans-serif",
                    color: '#1B2559',
                  }}
                >
                  Redirecting to Microsoft SSO
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: 'text.secondary',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Please wait while we successfully authenticate you on Microsoft SSO...
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 1 }}>
                    {error}
                  </Alert>
                )}
              </>
            )}
          </Paper>
        </Container>
      </Box>
    </>
  );
};

// Key features of this component:
// 1. Modal overlay with authentication form including email input
// 2. Multiple password-based authentication for different personas with email-based access tokens
// 3. Access token generation using email and persona combination (via apiService)
// 4. Email validation before authentication
// 5. Password visibility toggle
// 6. Error handling for missing email and incorrect passwords
// 7. Stores authentication state, persona, and access token in localStorage (email NOT stored)
// 8. Dispatches custom events for persona changes with access token data
// 9. Uses Material-UI components for consistent styling
// 10. Responsive design with Campbell branding

// export default PasswordModal;
//             If you don't have the password, please contact your administrator
//           </Typography>
//         </Paper>
//       </Container>
//     </Box>
//   );
// };

// Key features of this component:
// 1. Modal overlay with authentication form including email input
// 2. Multiple password-based authentication for different personas with email-based access tokens
// 3. Access token generation using email and persona combination
// 4. Email validation before authentication
// 5. Password visibility toggle
// 6. Error handling for missing email and incorrect passwords
// 7. Stores authentication state, persona, and access token in localStorage (email NOT stored)
// 8. Utility function to extract email from access token when needed
// 9. Dispatches custom events for persona changes with access token data
// 10. Uses Material-UI components for consistent styling
// 11. Responsive design with Campbell branding

// Export the utility function for use in other components
// export { getEmailFromAccessToken };

PasswordModal.propTypes = {
  onAuthenticated: PropTypes.func
};

export default PasswordModal;
